/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { AbstractObject, uuid } from "@src/app/model/abstract-object";
import { PicklistField, PicklistOptions } from "@src/app/shared/field/PicklistField";
import { required } from "@src/app/shared/validators";
import { ImportDoc, ImportDocNote } from "@src/app/modules/importer/ImportDoc";
import { ImportInput } from "@src/app/modules/importer/ImportInput";
import { ImportRow } from "@src/app/modules/importer/ImportRow";
import { ParseOptionBase, ParseValidator } from "@src/app/modules/importer/General/ParseOptionBase";
import { FormButtonComponent } from "@src/app/shared/form/form-button/form-button.component";
import { MyInjector } from "@src/app/app.module";
import { MatDialog } from "@angular/material/dialog";
import { BCodePageComponent } from "@src/app/modules/budget/b-code-page/b-code-page.component";
import { BCode } from "@src/app/model/bcode";
import { ConfirmDialogService } from "@src/app/shared/dialogs/confirmDialog";
import { BCodeService } from "@src/app/modules/budget/bcode.service";
import { FormComboBoxComponent } from "@src/app/shared/form/form-combo-box/form-combo-box.component";
import { ImportField } from "@src/app/modules/importer/ImportField";
import { ErrorCode } from "@src/app/shared/form/form-error/form-error.component";

export class ParseOptionBaseBCode extends ParseOptionBase {

    private importDoc: ImportDoc;
    private options: BCode[];
    private cds = MyInjector.instance.get(ConfirmDialogService);
    private bCodeSvc = MyInjector.instance.get(BCodeService);

    optionMap = new Map<string, BCode>();
    optionsUsed = new Map<uuid, BCode>();
    optionNotes: ImportDocNote[] = [];
    optionInputs: ImportInput[] = [];
    private _silent = false;

    allowNulls = false;

    constructor(public readonly name: string,
        public readonly fieldName,
        parseValidators: ParseValidator[] = []) {
        super(name, fieldName, parseValidators, () => {
            this.optionMap = new Map();
            this.optionsUsed = new Map();
            if (this.optionInputs.length > 0 || this.optionNotes.length > 0) {
                this.optionNotes.forEach(note => this.importDoc.deleteNote(note));
                this.optionNotes = [];
                this.optionInputs.forEach(input => this.importDoc.removeInput(input));
                this.optionInputs = [];
            }
        });
        this.addValidator(this.optionValidator);
    }
    allowNull() {
        this.allowNulls = true;
        return this;
    }
    makeSilent() {
        this._silent = true;
        return this;
    }

    getRowValue(row: ImportRow) {
        let rowValue = 'default_OPTION';
        if (this.mappedToColumn !== 'default_OPTION') {
            rowValue = row.getStringValue(this.mappedToColumn);
        }

        if (this.optionMap.has(rowValue)) {
            return this.optionMap.get(rowValue).id;
        } else {
            console.error('Could not find value ', { rowValue, pobm: this, row });
            return null
        }
    }

    setItemDefaultValue(row: ImportRow, item: AbstractObject) {
        const defaultMapping = this.optionMap.get('default_OPTION');
        if (defaultMapping) {
            item[this.fieldName] = defaultMapping.id;
        } else {
            console.error('Do not know how to map default value', { pobm: this, row, item });
        }
    }

    getOption(id: uuid) : BCode {
        return this.options.find( o => o.id === id);
    }

    setOptions(options: BCode[], importDoc: ImportDoc) {
        this.options = options;
        this.importDoc = importDoc;
    }

    createOptionMapperInput(optionValue: string, firstRow: ImportRow) {
        const newInput = new ImportInput(this.name + '.' + optionValue);
        newInput.setHelp($localize`Which '${this.name}' do you wish to import the value '${optionValue}' as`);

        const plRefresher = (o: BCode) => {
            console.log('Refresher called', o);
            if (o === null) {
                return;
            }
            if (!this.optionsUsed.has(o.id)) {
                const prevMsg = $localize `
                    WARNING: Budget Code '${o.name}' was previously mapped to '${o.integrationId}'
                    IF YOU PROCEED YOU WILL OVERWRITE THIS MAPPING
                `;
                const title = $localize`Assign ${optionValue} as integrationId`;
                const msg = $localize`Are you sure you want to assign '${optionValue}' to '${this.getBCodeDesc(o)}?
            All future imports will use this mapping to match to this budget code.
            ${o.integrationId && o.integrationId !== optionValue ? prevMsg : ''}
            `;
                const confirmMapping = () => {
                    this.bCodeSvc.put({
                        id: o.id,
                        integrationId: optionValue,
                        revision: o.revision
                    }).subscribe(() => {
                        this.importDoc.removeInput(newInput);
                        this.optionMap.set(optionValue, o);
                        this.noteImportAsBCode(firstRow, o);
                        this.noteMapping(optionValue, o);
                    });
                };
                const cancelMapping = () => {
                    console.log('Cancel mapping', { ctls: newInput.getControls() });
                    const cbo = newInput.getControls()[0].field as PicklistField;
                    cbo.clearComboValue();
                }
                const cdsData = {
                    title,
                    msg,
                    options: [
                        { name: $localize`OK`, action: confirmMapping},
                        { name: $localize`Cancel`, action: cancelMapping }
                    ]
                }
                this.cds.openChoice(cdsData, false);
            }
        }
        const plOptions = { items: this.options, refreshes: [plRefresher] } as Partial<PicklistOptions>;
        if (this.allowNulls) {
            plOptions.allowSelectNone = true;
        }
        plOptions.optionDisplayValue = this.getBCodeDesc.bind(this);
        const fldOptions = {
            validators: [required, (ctl) => {
                if (this.optionsUsed.has(ctl.value)) {
                    return new ErrorCode('AlreadyUsed', $localize`Already Mapped`).new();
                }
                return null;
            }],
        } as Partial<PicklistField>

        const label = $localize`'Budget Code to import '${optionValue}' as`;
        const field = FormComboBoxComponent.make(label, 'value', null, plOptions, fldOptions);

        newInput.addField(field);
        this.createOptionAdderInput(optionValue, plRefresher, newInput);

        this.importDoc.addInput(newInput);
        this.optionInputs.push(newInput);

    }

    createBCode(optionValue: string) {
        const dialog = MyInjector.instance.get(MatDialog);
        const dialogRef = dialog.open(BCodePageComponent,
            {
                data:
                {
                    name: optionValue,
                    hideTabs: true,
                    mode: 'new',
                    integrationId: optionValue,
                }
            }
        );
        return dialogRef.afterClosed();
    }

    createOptionAdderInput(optionValue: string, plRefresher, input: ImportInput) {

        const btn = FormButtonComponent.makeTextButton('Create ' + optionValue, 'optionValue', ( () => {
                this.createBCode(optionValue).subscribe( newItem => {
                    if (newItem) {
                        plRefresher(newItem);
                        this.importDoc.removeInput(input);
                    }
                });
            }).bind(this)
        );
        btn.makeControl().setValue('Or Create New');
        input.addField(btn);

    }

    matchBCode(value: string) {
        let match = this.options.find(o => o.integrationId === value);
        if (!match) {
            match = this.options.find(o => o.name === value);
        }
        return match;
    }

    optionValidator: ParseValidator = {
        validateRow: (cell, row) => {
            const cellValue = cell.getValue() as string;
            if (!this.optionMap.has(cellValue)) {
                if (!this.options) {
                    console.warn('Options have not been set, call setOptions on the POBMapped with available options', this);
                }
                const option = this.matchBCode(cellValue);
                this.optionMap.set(cellValue, option);
                if (option === undefined) {
                    //console.log('Option not found - Create Input', { cellValue, pob: this });
                    if (cellValue) {
                        this.createOptionMapperInput(cellValue, row);
                    }
                } else {
                    this.noteImportAsBCode(row, option);
                    if (!this._silent) {
                        this.noteMapping(cellValue, option);
                    }
                }
            } else {
                this.noteImportAsBCode(row, this.optionMap.get(cellValue));
            }
            return null;
        }
    }

    private noteImportAsBCode(row: ImportRow, bCode) {
        let noteField = row.getField('ImportsAsBCode');
        if (!noteField) {
            noteField = new ImportField('ImportsAsBCode');
            row.add(noteField);
        }
        noteField.setValue(this.getBCodeDesc(bCode));
    }

    private getBCodeDesc(bCode : BCode) {
        if (bCode) {
            return `${bCode.type.name}: ${bCode.name} ${bCode.description ? '(' + bCode.description + ')' : ''}`;
        }
        return '';
    }

    private noteMapping(optionValue: string, option: BCode) {
        const newNote = new ImportDocNote();
        newNote.note = $localize`[${optionValue}] imports as budget code [${this.getBCodeDesc(option)}]'`;
        this.importDoc.addNoteItem(newNote);
        this.optionsUsed.set(option.id, option);
        this.optionNotes.push(newNote);
    }
}
